import { useEffect, useState, useMemo } from "react";
import "./App.css";
import Storefront from "./Routes/Storefront/Storefront.Route";
import Coupon from "./Routes/Coupons/Coupon.Route";
import Signin from "./Pages/SignIn/SignIn.Page";
import Agreement from "./Pages/Registration/Agreement.Page";
import MemberSignUp from "./Components/Registration/MemberSignUp";
import Settings from "./Pages/Settings/Settings.Page";
import NotificationsRoute from "./Routes/Notifications/Notifications.Route";
import SharedLayout from "./Routes/SharedLayout/SharedLayout";
import DashboardRoute from "./Routes/Dashboard/Dashboard.Route";
import ReportAndAnalyticsRoute from "./Routes/ReportAndAnalytics/ReportAndAnalytics.Route";
import FinanceManagementRoute from "./Routes/FinanceManagement/FinanceManagement.Route";
import DropTrackProductsRoute from "./Routes/DropTrackProducts/DropTrackProducts.Route";
import OrderManagementRoute from "./Routes/OrderManagement/OrderManagement.Route";
import CollectionsRoute from "./Routes/Collections/Collections.Route";
import { useDispatch, useSelector } from "react-redux";
import { fetchNotifications } from "./Features/notificationsSlice";
import LensAndReviewsRoute from "./Routes/LensAndReviews/LensAndReviews.Route";
import { apiV1 } from "./Auth/Interceptor";
import { setUser } from "./Features/userSlice";
import Toaster from "./Components/Toaster/Toaster";
import {
    Routes,
    Route,
    useNavigationType,
    useLocation,
    Navigate,
    useNavigate,
} from "react-router-dom";
import InventoryManagementRoute from "./Routes/InventoryManagement/InventoryManagement.Route";
import ForgotPassword from "./Pages/ForgotPassword/ForgotPassword.Page";
import ChangePassword from "./Pages/ChangePassword/ChangePassword.Page";
import ApplySeller from "./Pages/ApplySeller/ApplySeller.Page";
import Error from "./Pages/Error/Error.Page";
import Loading from "./Components/Loading/Loading";
import { fetchInventoryProducts } from "./Features/inventoryManagementSlice";
import EditStore from './Pages/EditStore/EditStore';
import useAuthentication from "./Hooks/useAuthentication";
import { StorefrontContextProvider } from './Context/StorefrontContext';

import BusinessInformation from './Pages/Registration/BusinessInformation.Page';
import StoreSetup from "./Pages/Registration/StoreSetup.Page";
import BankDetails from "./Pages/Registration/BankDetails.Page";
import BasicDetails from "./Pages/Registration/BasicDetails.Page";
import 'leaflet/dist/leaflet.css';

const userProfile = async () => {
    try {
        const response = await apiV1.get("sellerMembers/profile");
        if (response.status !== 200) {
            throw new Error("Failed to fetch user details");
        }
        return response.data.data;
    } catch (error) {}
};

function App() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const { validToken, isLoading: authLoading } = useAuthentication();
    const action = useNavigationType();
    const location = useLocation();
    const pathname = location.pathname;
    const user = useSelector((state) => state.user.user);
    const memoizedUser = useMemo(() => user || {}, [user]);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (Object.keys(memoizedUser).length > 0) {
            setRoles(memoizedUser.role);
        }
    }, [memoizedUser]);

    useEffect(() => {
        if (action !== "POP") {
            window.scrollTo(0, 0);
        }
    }, [action, pathname]);

    useEffect(() => {
        const fetchData = async () => {
            // Only fetch data if user is authenticated
            if (validToken) {
                dispatch(fetchNotifications());
                const data = await userProfile();
                dispatch(setUser(data));
                dispatch(fetchInventoryProducts());
            }
            setLoading(false);
        };

        // Only run when auth loading is complete
        if (!authLoading) {
            fetchData();
        }
        // eslint-disable-next-line
    }, [validToken, authLoading]);

    useEffect(() => {
        let title = "";
        let metaDescription = "";

        switch (pathname) {
            case "/":
                title = "";
                metaDescription = "";
                break;
            default:
                break;
        }

        if (title) {
            document.title = title;
        }

        if (metaDescription) {
            const metaDescriptionTag = document.querySelector(
                'head > meta[name="description"]'
            );
            if (metaDescriptionTag) {
                metaDescriptionTag.content = metaDescription;
            }
        }
    }, [pathname, action]);

    const checkRoute = (role, route) => {
        const inventoryRoutes = [
            "/collections",
            "/droptrack-products",
            "/order-management",
            "/inventory-management",
            "/coupon-management",
        ];

        const lensRoutes = ["/lens-and-reviews", "/report-and-analytics"];

        const storeRoutes = ["/storefront", "/editstore"];

        if (role === "Admin") {
            return true;
        } else if (role === "Inventory Manager") {
            return inventoryRoutes.some((baseRoute) => {
                const regex = new RegExp(`^${baseRoute}(\\/.*)?$`);
                return regex.test(route);
            });
        } else if (role === "Lens Manager") {
            return lensRoutes.some((baseRoute) => {
                const regex = new RegExp(`^${baseRoute}(\\/.*)?$`);
                return regex.test(route);
            });
        } else if (role === "Store Manager") {
            return storeRoutes.some((baseRoute) => {
                const regex = new RegExp(`^${baseRoute}(\\/.*)?$`);
                return regex.test(route);
            });
        }
        return false;
    };

    useEffect(() => {
        const handleWheel = (e) => {
            const active = document.activeElement;
            if (active && active.type === "number" && active instanceof HTMLInputElement) {
                active.blur(); // Optional: remove focus
                e.preventDefault();
            }
        };

        window.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            window.removeEventListener("wheel", handleWheel);
        };
    }, []);

    useEffect(() => {
        if (!loading && pathname !== "/editStore") {
            if (roles.includes("Admin")) {
                if (location.pathname.split("/").length > 0) {
                    navigate(location.pathname);
                } else {
                    navigate("/");
                }
            } else if (roles.includes("Inventory Manager")) {
                if (location.pathname.split("/").length > 0) {
                    if (checkRoute("Inventory Manager", location.pathname)) {
                        navigate(location.pathname);
                    } else {
                        navigate("/inventory-management");
                    }
                } else {
                    navigate("/inventory-management");
                }
            } else if (roles.includes("Lens Manager")) {
                if (location.pathname.split("/").length > 0) {
                    if (checkRoute("Lens Manager", location.pathname)) {
                        navigate(location.pathname);
                    } else {
                        navigate("/lens-and-reviews");
                    }
                } else {
                    navigate("/lens-and-reviews");
                }
            } else if (roles.includes("Store Manager")) {
                if (location.pathname.split("/").length > 0) {
                    if (checkRoute("Store Manager", location.pathname)) {
                        navigate(location.pathname);
                    } else {
                        navigate("/storefront");
                    }
                } else {
                    navigate("/storefront");
                }
            }
        }
        //eslint-disable-next-line
    }, [roles, loading]);

    // Show loading while auth is being checked or data is being fetched
    if (authLoading || loading) {
        return (
            <div className="max-w-screen h-screen">
                <Loading />
            </div>
        );
    }

    return (
        <>
            <Toaster />

            <Routes>
                <Route path="/" element={validToken ? <SharedLayout /> : <Navigate to="/signin" replace />}>
                    {roles?.includes("Admin") && (
                        <>
                            <Route index element={<DashboardRoute />} />
                            <Route path="notifications" element={<NotificationsRoute />} />
                            <Route path="finance-management/*" element={<FinanceManagementRoute />} />
                            <Route path="storefront" element={<Storefront />} />
                        </>
                    )}

                    {(roles?.includes("Admin") || roles?.includes("Lens Manager")) && (
                        <>
                            <Route path="report-and-analytics/*" element={<ReportAndAnalyticsRoute />} />
                            <Route path="lens-and-reviews/*" element={<LensAndReviewsRoute />} />
                        </>
                    )}

                    {(roles?.includes("Admin") || roles?.includes("Inventory Manager")) && (
                        <>
                            <Route path="droptrack-products/*" element={<DropTrackProductsRoute />} />
                            <Route path="order-management/*" element={<OrderManagementRoute />} />
                            <Route path="collections/*" element={<CollectionsRoute />} />
                            <Route path="inventory-management/*" element={<InventoryManagementRoute />} />
                            <Route path="coupon-management/*" element={<Coupon />} />
                        </>
                    )}

                    <Route path="/settings" element={<Settings />} />
                </Route>

                {roles.includes("Store Manager") && <Route path="storefront" element={<Storefront />} />}

                {
                    (roles?.includes("Admin") || roles.includes("Store Manager")) && 
                    <Route 
                        path="editstore" 
                        element={
                            validToken ? 
                            <StorefrontContextProvider>
                                <EditStore />
                            </StorefrontContextProvider> : 
                            <Signin />
                        } 
                    />
                }

                {
                    !validToken && 
                    <>
                        <Route path="/signin" element={<Signin />} />

                        <Route path="/agreement" element={<Agreement />} />

                        <Route path="/registration" element={<BasicDetails />} />

                        <Route path="/businessinformation" element={<BusinessInformation />} />

                        <Route path="/storesetup" element={<StoreSetup />} />

                        <Route path="/bankdetails" element={<BankDetails />} />

                        <Route path='/forgot-password' element={<ForgotPassword />} />

                        <Route path='/change-password' element={<ChangePassword />} />

                        <Route path='/apply-seller' element={<ApplySeller />} />

                        <Route path="/member-signup" element={<MemberSignUp />} />

                        <Route path="/verifyEmail" element={<BusinessInformation />} />
                    </>
                }

                <Route path='*' element={<Error />} />
            </Routes>
        </>
    );
}

export default App;
